import { useState } from 'react';
import {
  Box, Typography, Button, Paper, Chip, Tabs, Tab,
  Table, TableBody, TableCell, TableHead, TableRow,
  Collapse, CircularProgress, Tooltip, Alert,
  Grid, Card, CardContent, CardActions,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  CheckCircle as CompletedIcon,
  Error as FailedIcon,
  HourglassEmpty as RunningIcon,
  Schedule as QueuedIcon,
  RemoveCircleOutline as CanceledIcon,
  SkipNext as SkippedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useJobs, useRunJob, useCancelJob, useSyncAccounts } from '@/hooks/useSync';
import { getErrorMessage } from '@/api/client';
import { formatDateRelative } from '@/utils/dateUtils';
import { DisplayStatus, RUNNABLE_JOB_TYPES, RunnableJobType, SyncJob, isJobActive } from '@/api/sync';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const HISTORY_LIMIT = 50;

const STATUS_STYLE: Record<
  DisplayStatus,
  { color: 'default' | 'success' | 'error' | 'warning' | 'info'; variant: 'filled' | 'outlined'; icon: JSX.Element }
> = {
  queued:    { color: 'default', variant: 'filled',   icon: <QueuedIcon fontSize="inherit" /> },
  running:   { color: 'warning', variant: 'filled',   icon: <RunningIcon fontSize="inherit" /> },
  completed: { color: 'success', variant: 'filled',   icon: <CompletedIcon fontSize="inherit" /> },
  failed:    { color: 'error',   variant: 'filled',   icon: <FailedIcon fontSize="inherit" /> },
  canceled:  { color: 'default', variant: 'outlined', icon: <CanceledIcon fontSize="inherit" /> },
  skipped:   { color: 'info',    variant: 'outlined', icon: <SkippedIcon fontSize="inherit" /> },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Naive server-local ISO strings — parsed as-is, never forced to UTC. */
function timeAgo(dt: string | null): string {
  if (!dt) return '—';
  return formatDateRelative(dt);
}

function formatDuration(job: SyncJob): string {
  if (!job.started_at || !job.completed_at) return '—';
  const s = Math.max(
    0,
    Math.round((new Date(job.completed_at).getTime() - new Date(job.started_at).getTime()) / 1000)
  );
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return sec > 0 ? `${m}m ${sec}s` : `${m}m`;
}

/** Progress fields worth showing as counters — everything but the `recent` feed. */
function progressCounters(progress: SyncJob['progress']): [string, string][] {
  if (!progress) return [];
  return Object.entries(progress)
    .filter(([key]) => key !== 'recent')
    .map(([key, value]) => [key, typeof value === 'object' ? JSON.stringify(value) : String(value)]);
}

interface RecentEntry {
  ts?: string;
  [key: string]: unknown;
}

function recentFeed(progress: SyncJob['progress']): RecentEntry[] {
  const recent = progress?.recent;
  return Array.isArray(recent) ? (recent as RecentEntry[]) : [];
}

function recentLine(entry: RecentEntry): string {
  return Object.entries(entry)
    .filter(([key]) => key !== 'ts')
    .map(([key, value]) => `${key}=${typeof value === 'object' ? JSON.stringify(value) : String(value)}`)
    .join(' ');
}

/** `recent[].ts` is naive server-local, same basis as the job's own timestamps. */
function recentTime(ts: string | undefined): string {
  if (!ts) return '';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleTimeString();
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatusChip({ status, t }: { status: DisplayStatus; t: TFunction<'pages'> }) {
  const style = STATUS_STYLE[status];
  return (
    <Chip
      size="small"
      icon={style.icon}
      color={style.color}
      variant={style.variant}
      label={t(`sync.status.${status}`)}
    />
  );
}

function JobTypeLabel({ jobType, t }: { jobType: string; t: TFunction<'pages'> }) {
  return (
    <Box>
      <Typography variant="body2">
        {t(`sync.jobTypes.${jobType}`, { defaultValue: jobType })}
      </Typography>
      <Typography variant="caption" color="text.secondary" fontFamily="monospace" dir="ltr">
        {jobType}
      </Typography>
    </Box>
  );
}

function JobRow({ job, t, accountLabel }: { job: SyncJob; t: TFunction<'pages'>; accountLabel: string }) {
  const [open, setOpen] = useState(false);
  const details = job.result ?? job.progress;
  const expandable = !!details || !!job.error;

  return (
    <>
      <TableRow
        hover
        onClick={() => expandable && setOpen((o) => !o)}
        sx={{ cursor: expandable ? 'pointer' : 'default' }}
      >
        <TableCell><JobTypeLabel jobType={job.job_type} t={t} /></TableCell>
        <TableCell><Typography variant="caption" dir="ltr">{accountLabel}</Typography></TableCell>
        <TableCell><StatusChip status={job.display_status} t={t} /></TableCell>
        <TableCell><Typography variant="caption">{timeAgo(job.created_at)}</Typography></TableCell>
        <TableCell><Typography variant="caption">{timeAgo(job.started_at)}</Typography></TableCell>
        <TableCell><Typography variant="caption">{formatDuration(job)}</Typography></TableCell>
        <TableCell>
          <Typography variant="caption" dir="ltr">
            {job.created_by === null ? '—' : `#${job.created_by}`}
          </Typography>
        </TableCell>
        <TableCell sx={{ maxWidth: 220 }}>
          <Tooltip title={job.error ?? ''}>
            <Typography variant="caption" color="error" noWrap display="block">
              {job.error ?? ''}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          {expandable
            ? (open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />)
            : <Typography variant="caption" color="text.secondary">—</Typography>}
        </TableCell>
      </TableRow>
      {expandable && (
        <TableRow>
          <TableCell colSpan={9} sx={{ p: 0, border: 0 }}>
            <Collapse in={open} unmountOnExit>
              <Box
                dir="ltr"
                sx={{
                  bgcolor: 'grey.900', color: 'grey.100', fontFamily: 'monospace',
                  fontSize: '0.72rem', p: 2, maxHeight: 260, overflow: 'auto',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                }}
              >
                {job.error ? `error: ${job.error}\n\n` : ''}
                {job.result ? `result: ${JSON.stringify(job.result, null, 2)}\n\n` : ''}
                {job.progress ? `progress: ${JSON.stringify(job.progress, null, 2)}` : ''}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function ActiveJobPanel({
  job, t, onCancel, canceling,
}: {
  job: SyncJob;
  t: TFunction<'pages'>;
  onCancel: (id: number) => void;
  canceling: boolean;
}) {
  const counters = progressCounters(job.progress);
  const feed = recentFeed(job.progress);

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <JobTypeLabel jobType={job.job_type} t={t} />
        </Box>
        <StatusChip status={job.display_status} t={t} />
        <Tooltip title={t('sync.cancelHelp')}>
          <span>
            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={canceling ? <CircularProgress size={14} color="inherit" /> : <CancelIcon />}
              disabled={canceling}
              onClick={() => onCancel(job.id)}
            >
              {t('sync.cancel')}
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        {t('sync.queuedAt')}: {timeAgo(job.created_at)}
        {job.started_at ? ` · ${t('sync.startedAt')}: ${timeAgo(job.started_at)}` : ''}
      </Typography>

      {counters.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
          {counters.map(([key, value]) => (
            <Chip
              key={key}
              size="small"
              variant="outlined"
              dir="ltr"
              label={`${key}: ${value}`}
              sx={{ fontFamily: 'monospace' }}
            />
          ))}
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
        {t('sync.activity')}
      </Typography>
      <Box
        dir="ltr"
        sx={{
          mt: 0.5, bgcolor: 'grey.900', color: 'grey.100', fontFamily: 'monospace',
          fontSize: '0.72rem', lineHeight: 1.6, p: 1.5, borderRadius: 1,
          maxHeight: 220, overflow: 'auto',
        }}
      >
        {feed.length === 0 ? (
          <Box sx={{ opacity: 0.6 }}>{t('sync.noActivity')}</Box>
        ) : (
          feed.map((entry, i) => (
            <Box key={i} sx={{ wordBreak: 'break-all' }}>
              <span style={{ opacity: 0.45, marginInlineEnd: 8 }}>{recentTime(entry.ts)}</span>
              {recentLine(entry)}
            </Box>
          ))
        )}
      </Box>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

interface Notice {
  severity: 'success' | 'info' | 'error';
  message: string;
}

export default function SyncPage() {
  const { t } = useTranslation('pages');
  const { data: jobs = [], isLoading } = useJobs(undefined, HISTORY_LIMIT);
  // /sync/jobs spans every manuscript account, so a row here can belong to an
  // account this page never runs — name it rather than let it read as ours.
  const { data: accounts = [] } = useSyncAccounts();
  const accountName = (id: number | null): string => {
    if (id === null) return t('sync.accountUnassigned');
    return accounts.find((a) => a.id === id)?.label ?? `#${id}`;
  };
  // Per-type latest rows come from their own limit-1 queries: the unfiltered
  // top-N history list can be starved of find_* rows by a big join_channel
  // batch (dozens of queued joins are all newer than the last find_* run).
  // Pinned to the DEFAULT account — the one POST /sync/run/* queues to — so the
  // panel and its Run/Cancel buttons describe the same sweep. Held until the
  // account list resolves: unfiltered, these return another account's scheduled
  // sweep and the Cancel button would stop that instead.
  const defaultAccountId = accounts.find((a) => a.is_default)?.id;
  const scoped = defaultAccountId != null;
  const latestQueries = {
    find_missing: useJobs('find_missing', 1, scoped, defaultAccountId),
    find_new: useJobs('find_new', 1, scoped, defaultAccountId),
    insert_missing: useJobs('insert_missing', 1, scoped, defaultAccountId),
    sync_dialogs: useJobs('sync_dialogs', 1, scoped, defaultAccountId),
  } as const;
  const { mutate: run, isPending: running } = useRunJob();
  const { mutate: cancel } = useCancelJob();

  const [activeTab, setActiveTab] = useState<'run' | 'history'>('run');
  const [cancelingId, setCancelingId] = useState<number | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);

  const latestFor = (jobType: RunnableJobType): SyncJob | undefined =>
    latestQueries[jobType].data?.[0];

  // Runnable jobs only — the join queue has its own UI (JoinChannelDialog),
  // and a link batch would otherwise render dozens of panels here.
  const activeJobs = RUNNABLE_JOB_TYPES
    .map((jobType) => latestFor(jobType))
    .filter((j): j is SyncJob => !!j && isJobActive(j));

  const handleRun = (jobType: RunnableJobType) => {
    setNotice(null);
    run(
      { jobType },
      {
        onSuccess: (res) => {
          const label = t(`sync.jobTypes.${jobType}`, { defaultValue: jobType });
          if (res.duplicate) {
            setNotice({
              severity: 'info',
              message: res.existing
                ? t('sync.runDuplicate', {
                    job: label,
                    status: t(`sync.status.${res.existing.status}`, {
                      defaultValue: res.existing.status,
                    }),
                    id: res.existing.id,
                  })
                : t('sync.runDuplicateUnknown', { job: label }),
            });
          } else {
            setNotice({ severity: 'success', message: t('sync.runQueued', { id: res.id }) });
          }
        },
        onError: (err) => setNotice({ severity: 'error', message: getErrorMessage(err) }),
      }
    );
  };

  const handleCancel = (jobId: number) => {
    setNotice(null);
    setCancelingId(jobId);
    cancel(jobId, {
      onSuccess: () => setNotice({ severity: 'success', message: t('sync.canceled', { id: jobId }) }),
      onError: (err) =>
        setNotice({ severity: 'error', message: `${t('sync.cancelFailed')} — ${getErrorMessage(err)}` }),
      onSettled: () => setCancelingId(null),
    });
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={600}>{t('sync.title')}</Typography>
        {activeJobs.length > 0 && (
          <Chip
            size="small"
            color="warning"
            icon={<CircularProgress size={12} />}
            label={t('sync.activeCount', { count: activeJobs.length })}
          />
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" mb={2}>
        {t('sync.intro')}
      </Typography>

      {notice && (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.message}
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={t('sync.tabs.run')} value="run" />
        <Tab label={t('sync.tabs.history')} value="history" />
      </Tabs>

      {/* ---------------------------------------------------------------- */}
      {/* RUN TAB                                                           */}
      {/* ---------------------------------------------------------------- */}
      {activeTab === 'run' && (
        <Box>
          <Grid container spacing={2}>
            {RUNNABLE_JOB_TYPES.map((jobType) => {
              const latest = latestFor(jobType);
              return (
                <Grid item xs={12} sm={6} md={4} key={jobType}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent sx={{ pb: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                        <JobTypeLabel jobType={jobType} t={t} />
                        {latest
                          ? <StatusChip status={latest.display_status} t={t} />
                          : <Chip size="small" label={t('sync.status.never')} />}
                      </Box>
                      {latest && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          {t('sync.lastRun')}: {timeAgo(latest.started_at ?? latest.created_at)}
                          {' · '}
                          {formatDuration(latest)}
                        </Typography>
                      )}
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PlayIcon />}
                        disabled={running}
                        onClick={() => handleRun(jobType)}
                      >
                        {t('sync.run')}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {activeJobs.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>
                {t('sync.activeTitle')}
              </Typography>
              {activeJobs.map((job) => (
                <ActiveJobPanel
                  key={job.id}
                  job={job}
                  t={t}
                  onCancel={handleCancel}
                  canceling={cancelingId === job.id}
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* HISTORY TAB                                                       */}
      {/* ---------------------------------------------------------------- */}
      {activeTab === 'history' && (
        <Box>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : jobs.length === 0 ? (
            <Typography color="text.secondary">{t('sync.noJobs')}</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('sync.columns.job')}</TableCell>
                  <TableCell>{t('sync.columns.account')}</TableCell>
                  <TableCell>{t('sync.columns.status')}</TableCell>
                  <TableCell>{t('sync.columns.created')}</TableCell>
                  <TableCell>{t('sync.columns.started')}</TableCell>
                  <TableCell>{t('sync.columns.duration')}</TableCell>
                  <TableCell>{t('sync.columns.createdBy')}</TableCell>
                  <TableCell>{t('sync.columns.error')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((job) => (
                  <JobRow key={job.id} job={job} t={t} accountLabel={accountName(job.account_id)} />
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      )}
    </Box>
  );
}
